import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, updateDoc, collection, query, where, getDocs, getDoc, addDoc, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatCurrency} from '../Help/helper';
import { useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import BreadCrumb from '../BreadCrumb';
import { generateInvoiceNumber } from './Utils/generateInvoiceNumber';
import logger from '../../utils/logger';

const pages = [
  { name: 'Finance', to: '/finance', component: Link, current: false },
  { name: 'Quotations Admin', to: '/finance/quotations', component: Link, current: false }, 
  { name: 'Quotation Details', to: '/finance/quotations', component: Link, current: true },   
];


const QuotationDetails = () => {
  const { id } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const docRef = doc(db, 'quotations', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setQuotation(docSnap.data());
      } else {
        logger.warn('No such document!');
      }
    };

    fetchData();
  }, [id]);

  // Fetch VIN from vehicles collection if not in quotation
  useEffect(() => {
    const fetchVehicleData = async () => {
      if (quotation && quotation.jobCardNo && !quotation.vin) {
        try {
          // Query for job card using jobCardNo field
          const jobsRef = collection(db, 'jobs');
          const q = query(jobsRef, where('jobCardNo', '==', quotation.jobCardNo), limit(1));
          const jobSnapshot = await getDocs(q);

          if (!jobSnapshot.empty) {
            const jobData = jobSnapshot.docs[0].data();

            if (jobData.vehicleId) {
              // Fetch vehicle data using vehicleId
              const vehicleRef = doc(db, 'vehicles', jobData.vehicleId);
              const vehicleSnap = await getDoc(vehicleRef);

              if (vehicleSnap.exists()) {
                const vin = vehicleSnap.data().VIN || 'N/A';
                // Update the quotation state with VIN
                setQuotation(prev => ({ ...prev, vin: vin }));
              }
            }
          }
        } catch (error) {
          logger.error('Error fetching VIN:', error);
        }
      }
    };

    fetchVehicleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation?.jobCardNo]);

  const handleSyncVehicleData = async () => {
    if (!quotation || !quotation.jobCardNo) {
      toast.error('Unable to sync: Job card information is missing');
      return;
    }

    setIsSyncing(true);
    try {
      // Query for job card using jobCardNo field
      const jobsRef = collection(db, 'jobs');
      const q = query(jobsRef, where('jobCardNo', '==', quotation.jobCardNo), limit(1));
      const jobSnapshot = await getDocs(q);

      if (!jobSnapshot.empty) {
        const jobData = jobSnapshot.docs[0].data();

        if (jobData.vehicleId) {
          // Fetch vehicle data using vehicleId
          const vehicleRef = doc(db, 'vehicles', jobData.vehicleId);
          const vehicleSnap = await getDoc(vehicleRef);

          if (vehicleSnap.exists()) {
            const vehicleData = vehicleSnap.data();
            const vin = vehicleData.VIN || '';
            const engineType = vehicleData.engineType || '';

            // Update quotation in Firestore
            const quotationRef = doc(db, 'quotations', id);
            await updateDoc(quotationRef, {
              vin: vin,
              engineType: engineType
            });

            // Update local state
            setQuotation(prev => ({
              ...prev,
              vin: vin || 'N/A',
              engineType: engineType || 'N/A'
            }));

            toast.success('Vehicle data synced successfully!');
          } else {
            toast.error('Vehicle data not found');
          }
        } else {
          toast.error('Job card does not have vehicle information');
        }
      } else {
        toast.error(`Job card ${quotation.jobCardNo} not found in database`);
      }
    } catch (error) {
      logger.error('Error syncing vehicle data:', error);
      toast.error(`Failed to sync vehicle data: ${error.message}`);
    }
    setIsSyncing(false);
  };

  const handleApproveQuotation = async () => {
    try {
      const uniqueInvoiceNumber = await generateInvoiceNumber(db);      
      // Update the quotation's approval status
      const quotationRef = doc(db, 'quotations', id);
      await updateDoc(quotationRef, {
        approvalStatus: "Yes"
      });
  
      // Update the job's approval status
      const jobsRef = collection(db, 'jobs');
      const q = query(jobsRef, where('jobCardNo', '==', quotation.jobCardNo));
      const querySnapshot = await getDocs(q);
  
      const updatePromises = querySnapshot.docs.map(async (docSnapshot) => {
        const jobRef = doc(db, 'jobs', docSnapshot.id);
        await updateDoc(jobRef, {
          approvalStatus: "Yes",
          approvalDate: new Date(),
        });
      });
      await Promise.all(updatePromises);

      const totalreceivable = (quotation.cartItems || []).reduce((total, item) => {
        return total + ((item.salePrice || 0) * (item.quantity || 0));
      }, 0) + (quotation.taxAmount || 0);
     

      const invoiceData = {
        approvalStatus: "Yes",
        customerEmail: quotation.customerEmail || '',
        approvalDate: Timestamp.fromDate(new Date()),
        customerName: quotation.customerName || '',
        customerAddress: quotation.customerAddress || '',
        jobCardNo: quotation.jobCardNo || '',
        commissionPaid: "No",
        vehicleMake: quotation.vehicleMake || '',
        vehicleModel: quotation.vehicleModel || '',
        vehicleYear: quotation.vehicleYear || '',
        registrationNumber: quotation.registrationNumber || '',
        entryMileage: quotation.entryMileage || '',
        engineType: quotation.engineType || '',
        driverName: quotation.driverName || '',
        driverNumber: quotation.driverNumber || '',
        cartItems: (quotation.cartItems || []).map(item => ({
          description: item.description || '',
          costPrice: item.costPrice || 0,
          price: item.salePrice || 0,
          quantity: item.quantity || 0,
          type: item.type || 'default',
        })),
        subtotal: quotation.subtotal || 0,
        taxAmount: quotation.taxAmount || 0,
        includeTax: quotation.includeTax || false,
        taxOnServicesOnly: quotation.taxOnServicesOnly || false,
        invoiceNumber: uniqueInvoiceNumber,
        invoiceStatus: "Pending",
        paymentsReceived: [],
        totalSale: quotation.totalSale || 0,
        datePosted: Timestamp.fromDate(new Date()),
        receivable: totalreceivable,
        invoiceDate: Timestamp.fromDate(new Date()),
        totalCost: quotation.totalCost || 0,
      };
  
      // Create new invoice document in Firestore
      const docRef =  await addDoc(collection(db, 'newInvoices'), invoiceData);  
      toast.success('Quotation approved and invoice generated successfully');
      setTimeout(() => {
        navigate(`/finance/invoices/${docRef.id}`);
      }, 5000);  
    } catch (error) {
      logger.error('Error approving quotation: ', error);
      toast.error('Error approving quotation.');
    }
  };
  

  if (!quotation) return null; 

  return (
    <>
<BreadCrumb pages={pages} />

    <div className="p-6 bg-white rounded shadow-md">
      <h2 className="text-2xl font-bold mb-4">Quotation #{quotation.jobCardNo}</h2>

      {quotation.approvalStatus === "Yes" ? (
        <div className="rounded bg-green-500 text-white text-sm py-1 px-2">
          Approved
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => navigate(`/finance/edit-quotation/${id}`)}
            className="rounded bg-yellow-500 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-yellow-400"
          >
            Edit
          </button>

          <button
            type="button"
            onClick={handleApproveQuotation}
            className="rounded bg-green-500 ml-8 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-green-600"
          >
            Approve Quotation and Raise Invoice
          </button>
        </>
      )}


      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="col-span-2 md:col-span-1">
          <p className="text-gray-600 mb-2"><strong>Customer:</strong> <span className='font-semibold'> {quotation.customerName} </span></p>
        </div>
        <div className="col-span-2 md:col-span-1">
          <p className="text-gray-600 mb-2"><strong> Email:</strong> <span className='text-sm'> {quotation?.customerEmail} </span></p>
        </div>

          <div className="col-span-2 md:col-span-1">
          <p className="text-gray-600 mb-2"><strong>Posted By:</strong> <span className='text-sm'> {quotation.createdBy} </span></p>
        </div>



        <div className="col-span-2 md:col-span-1">
          <p className="text-gray-600 mb-2"><strong>Date:</strong> <span className='text-sm'> {new Date(quotation.dateCreated?.seconds * 1000).toLocaleDateString()} </span></p>
        </div>
      </div>

      {/* Vehicle and Driver Information */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-gray-700">Vehicle & Driver Information</h3>
          <button
            type="button"
            onClick={handleSyncVehicleData}
            disabled={isSyncing}
            className={`rounded px-3 py-1.5 text-sm font-semibold text-white shadow-sm ${
              isSyncing
                ? 'bg-blue-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSyncing ? 'Syncing...' : 'Sync Vehicle Data'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <p className="text-gray-600 text-sm"><strong>Vehicle:</strong> <span className='font-semibold'>{quotation.vehicleMake} {quotation.vehicleModel} {quotation.vehicleYear ? `(${quotation.vehicleYear})` : ''}</span></p>
          </div>
          <div>
            <p className="text-gray-600 text-sm"><strong>Engine Type:</strong> <span className='font-semibold'>{quotation.engineType || 'N/A'}</span></p>
          </div>
          <div>
            <p className="text-gray-600 text-sm"><strong>Entry Mileage:</strong> <span className='font-semibold'>{quotation.entryMileage || 'N/A'} km</span></p>
          </div>
          <div>
            <p className="text-gray-600 text-sm"><strong>Driver Name:</strong> <span className='font-semibold'>{quotation.driverName || 'N/A'}</span></p>
          </div>
          <div>
            <p className="text-gray-600 text-sm"><strong>Driver Phone:</strong> <span className='font-semibold'>{quotation.driverNumber || 'N/A'}</span></p>
          </div>
          <div>
            <p className="text-gray-600 text-sm"><strong>Registration:</strong> <span className='font-semibold'>{quotation.registrationNumber}</span></p>
          </div>
          <div>
            <p className="text-gray-600 text-sm"><strong>VIN Number:</strong> <span className='font-semibold'>{quotation.vin || 'N/A'}</span></p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
          <h3 className="font-bold mb-2 uppercase">Quotation Items</h3>
          <table className="w-full">
            <thead className="text-left border-b-2 border-gray-300">
              <tr>
               
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Quantity</th>
                <th className="px-4 py-3">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {quotation.cartItems.map((item, index) => (
                <tr key={index}>
                  <td className="px-4 py-3">{item.description}</td>
                 <td className="px-4 py-3">{formatCurrency(item.salePrice)}</td>
                  <td className="px-4 py-3">{item.quantity}</td>
                  <td className="px-4 py-3">{formatCurrency(item.quantity * item.salePrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-right">
        <p className='uppercase text-gray-600 font-semibold'>Subtotal (Excl VAT): {formatCurrency(quotation.subtotal)}</p>
        {quotation.includeTax && (
          <>
            <p className='text-xs text-gray-500'>VAT Applied: {quotation.taxOnServicesOnly ? 'Services/Labour Only' : 'All Items'}</p>
            <p className='text-sm font-semibold text-gray-600'>7.5% VAT: {formatCurrency(quotation?.taxAmount || 0)}</p>
          </>
        )}
        {!quotation.includeTax && (
          <p className='text-sm font-semibold text-gray-600'>7.5% VAT: ₦0.00</p>
        )}
        <h2 className="text-xl font-bold mb-2 uppercase">Grand Total: {formatCurrency(quotation.totalSale)}</h2>
      </div>
      <ToastContainer />
    </div>
    </>
  );
};

export default QuotationDetails;
